import React, { useState } from 'react';
import { 
  Plane, Building2, Bus, FileText, ArrowLeft, Check, Clock, 
  MapPin, Star, Users, CreditCard, User
} from 'lucide-react';
import { Button } from './ui/button';
import { useCurrency } from '../context/CurrencyContext';

const BookingSummary = ({ 
  packageData, 
  searchParams,
  onBack,
  onConfirmBooking 
}) => {
  const { formatPrice } = useCurrency();
  const [showCustomerForm, setShowCustomerForm] = useState(false);
  const [customerDetails, setCustomerDetails] = useState({
    leadPassenger: {
      title: 'Mr',
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      dateOfBirth: ''
    },
    additionalPassengers: [],
    specialRequests: '',
    termsAccepted: false
  });

  // Calculate totals - INCLUDING INFANTS
  const passengers = (searchParams?.adults || 2) + (searchParams?.children || 0) + (searchParams?.infants || 0);
  
  const flightTotal = (packageData?.outboundFlight?.pricing?.total_fare || 0) + 
                      (packageData?.inboundFlight?.pricing?.total_fare || 0);
  
  const hotelTotal = (packageData?.makkahRoom?.total_price || packageData?.makkahHotel?.total_price * passengers || 0) +
                     (packageData?.madinahRoom?.total_price || packageData?.madinahHotel?.total_price * passengers || 0);
  
  const transportTotal = (packageData?.transport?.price || 0) * (packageData?.selectedTransfers?.length || 3);
  
  const ziyaratTotal = (packageData?.selectedZiyarat || []).reduce((sum, tourId) => {
    if (tourId === 'makkah-ziyarat') return sum + 45 * passengers;
    if (tourId === 'madinah-ziyarat') return sum + 40 * passengers;
    return sum;
  }, 0);

  const visaTotal = packageData?.visa?.price * passengers || searchParams?.visaCost || 0;
  
  const subtotal = flightTotal + hotelTotal + transportTotal + ziyaratTotal + visaTotal;
  const serviceFee = Math.round(subtotal * 0.05);
  const totalPackage = subtotal + serviceFee;
  const pricePerPerson = Math.round(totalPackage / passengers);
  const depositAmount = 50 * passengers;

  // Initialize additional passengers when form opens
  const initializePassengers = () => {
    const additionalCount = passengers - 1;
    const newPassengers = [];
    const adultsCount = searchParams?.adults || 2;
    const childrenCount = searchParams?.children || 0;
    
    for (let i = 0; i < additionalCount; i++) {
      let type = 'adult';
      if (i >= adultsCount - 1 && i < adultsCount - 1 + childrenCount) {
        type = 'child';
      } else if (i >= adultsCount - 1 + childrenCount) {
        type = 'infant';
      }
      
      newPassengers.push({
        title: type === 'infant' ? 'Master' : 'Mr',
        firstName: '',
        lastName: '',
        dateOfBirth: '',
        type: type
      });
    }
    setCustomerDetails(prev => ({
      ...prev,
      additionalPassengers: newPassengers
    }));
    setShowCustomerForm(true);
  };

  const handleInputChange = (field, value, passengerIndex = null) => {
    if (passengerIndex === null) {
      setCustomerDetails(prev => ({
        ...prev,
        leadPassenger: { ...prev.leadPassenger, [field]: value }
      }));
    } else {
      setCustomerDetails(prev => {
        const updated = [...prev.additionalPassengers];
        updated[passengerIndex] = { ...updated[passengerIndex], [field]: value };
        return { ...prev, additionalPassengers: updated };
      });
    }
  };

  const handleSubmitBooking = () => {
    if (!customerDetails.termsAccepted) {
      alert('Please accept the terms and conditions');
      return;
    }
    if (!customerDetails.leadPassenger.firstName || !customerDetails.leadPassenger.lastName || 
        !customerDetails.leadPassenger.email || !customerDetails.leadPassenger.phone) {
      alert('Please fill in all required fields for the lead passenger');
      return;
    }
    onConfirmBooking && onConfirmBooking({
      package: packageData,
      searchParams,
      customer: customerDetails,
      pricing: {
        flightTotal,
        hotelTotal,
        transportTotal,
        ziyaratTotal,
        visaTotal,
        subtotal,
        serviceFee,
        totalPackage,
        depositAmount
      }
    });
  };

  // Customer Details Form with Theme Colors
  if (showCustomerForm) {
    return (
      <div className="min-h-screen bg-[#0a1628] py-6 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="bg-gradient-to-r from-[#1a2d4a] to-[#243b5c] rounded-t-2xl p-6 border border-[#d4af37]/30">
            <button 
              onClick={() => setShowCustomerForm(false)}
              className="flex items-center gap-2 text-[#d4af37] hover:text-[#e5c158] mb-4"
            >
              <ArrowLeft className="w-5 h-5" />
              Back to Summary
            </button>
            <h1 className="text-2xl font-bold flex items-center gap-3 text-white">
              <Users className="w-7 h-7 text-[#d4af37]" />
              Passenger Details
            </h1>
            <p className="text-gray-400 mt-1">Enter details for all {passengers} passenger(s)</p>
          </div>

          <div className="bg-[#1a2d4a] rounded-b-2xl shadow-lg p-6 space-y-8 border-x border-b border-[#d4af37]/30">
            {/* Lead Passenger */}
            <div className="border-b border-[#d4af37]/20 pb-6">
              <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <User className="w-5 h-5 text-[#d4af37]" />
                Lead Passenger (Adult 1)
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Title *</label>
                  <select 
                    value={customerDetails.leadPassenger.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    className="w-full px-3 py-2 bg-[#0a1628] border border-[#d4af37]/30 rounded-lg text-white focus:ring-2 focus:ring-[#d4af37] focus:border-[#d4af37]"
                  >
                    <option value="Mr">Mr</option>
                    <option value="Mrs">Mrs</option>
                    <option value="Ms">Ms</option>
                    <option value="Miss">Miss</option>
                    <option value="Dr">Dr</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">First Name *</label>
                  <input 
                    type="text"
                    value={customerDetails.leadPassenger.firstName}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    className="w-full px-3 py-2 bg-[#0a1628] border border-[#d4af37]/30 rounded-lg text-white focus:ring-2 focus:ring-[#d4af37] focus:border-[#d4af37]"
                    placeholder="As per passport"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Last Name *</label>
                  <input 
                    type="text"
                    value={customerDetails.leadPassenger.lastName}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    className="w-full px-3 py-2 bg-[#0a1628] border border-[#d4af37]/30 rounded-lg text-white focus:ring-2 focus:ring-[#d4af37] focus:border-[#d4af37]"
                    placeholder="As per passport"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Email *</label>
                  <input 
                    type="email"
                    value={customerDetails.leadPassenger.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className="w-full px-3 py-2 bg-[#0a1628] border border-[#d4af37]/30 rounded-lg text-white focus:ring-2 focus:ring-[#d4af37] focus:border-[#d4af37]"
                    placeholder="your@email.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Phone *</label>
                  <input 
                    type="tel"
                    value={customerDetails.leadPassenger.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    className="w-full px-3 py-2 bg-[#0a1628] border border-[#d4af37]/30 rounded-lg text-white focus:ring-2 focus:ring-[#d4af37] focus:border-[#d4af37]"
                    placeholder="+44 7XXX XXX XXX"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Date of Birth *</label>
                  <input 
                    type="date"
                    value={customerDetails.leadPassenger.dateOfBirth}
                    onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
                    className="w-full px-3 py-2 bg-[#0a1628] border border-[#d4af37]/30 rounded-lg text-white focus:ring-2 focus:ring-[#d4af37] focus:border-[#d4af37]"
                  />
                </div>
              </div>
            </div>

            {/* Additional Passengers */}
            {customerDetails.additionalPassengers.map((passenger, index) => (
              <div key={index} className="border-b border-[#d4af37]/20 pb-6">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <User className="w-5 h-5 text-[#d4af37]" />
                  {passenger.type === 'adult' ? `Adult ${index + 2}` : 
                   passenger.type === 'child' ? `Child ${index + 2 - (searchParams?.adults || 2) + 1}` :
                   `Infant ${index + 2 - (searchParams?.adults || 2) - (searchParams?.children || 0) + 1}`}
                  <span className="text-xs bg-[#d4af37]/20 text-[#d4af37] px-2 py-0.5 rounded-full ml-2">
                    {passenger.type === 'infant' ? '0-2 yrs' : passenger.type === 'child' ? '2-11 yrs' : '12+ yrs'}
                  </span>
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Title</label>
                    <select 
                      value={passenger.title}
                      onChange={(e) => handleInputChange('title', e.target.value, index)}
                      className="w-full px-3 py-2 bg-[#0a1628] border border-[#d4af37]/30 rounded-lg text-white focus:ring-2 focus:ring-[#d4af37]"
                    >
                      <option value="Mr">Mr</option>
                      <option value="Mrs">Mrs</option>
                      <option value="Ms">Ms</option>
                      <option value="Miss">Miss</option>
                      <option value="Master">Master</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">First Name *</label>
                    <input 
                      type="text"
                      value={passenger.firstName}
                      onChange={(e) => handleInputChange('firstName', e.target.value, index)}
                      className="w-full px-3 py-2 bg-[#0a1628] border border-[#d4af37]/30 rounded-lg text-white focus:ring-2 focus:ring-[#d4af37]"
                      placeholder="As per passport"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Last Name *</label>
                    <input 
                      type="text"
                      value={passenger.lastName}
                      onChange={(e) => handleInputChange('lastName', e.target.value, index)}
                      className="w-full px-3 py-2 bg-[#0a1628] border border-[#d4af37]/30 rounded-lg text-white focus:ring-2 focus:ring-[#d4af37]"
                      placeholder="As per passport"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Date of Birth *</label>
                    <input 
                      type="date"
                      value={passenger.dateOfBirth}
                      onChange={(e) => handleInputChange('dateOfBirth', e.target.value, index)}
                      className="w-full px-3 py-2 bg-[#0a1628] border border-[#d4af37]/30 rounded-lg text-white focus:ring-2 focus:ring-[#d4af37]"
                    />
                  </div>
                </div>
              </div>
            ))}

            {/* Special Requests */}
            <div>
              <h3 className="text-lg font-bold text-white mb-4">Special Requests (Optional)</h3>
              <textarea
                value={customerDetails.specialRequests}
                onChange={(e) => setCustomerDetails(prev => ({ ...prev, specialRequests: e.target.value }))}
                className="w-full px-3 py-2 bg-[#0a1628] border border-[#d4af37]/30 rounded-lg text-white focus:ring-2 focus:ring-[#d4af37] h-24"
                placeholder="Dietary requirements, wheelchair assistance, room preferences, etc."
              />
            </div>

            {/* Terms and Conditions */}
            <div className="bg-[#0a1628] rounded-xl p-4 border border-[#d4af37]/30">
              <label className="flex items-start gap-3 cursor-pointer">
                <input 
                  type="checkbox"
                  checked={customerDetails.termsAccepted}
                  onChange={(e) => setCustomerDetails(prev => ({ ...prev, termsAccepted: e.target.checked }))}
                  className="w-5 h-5 mt-0.5 text-[#d4af37] rounded border-[#d4af37]/50"
                />
                <span className="text-sm text-gray-300">
                  I confirm that all passenger details are correct and match their passports. 
                  I have read and accept the <a href="#" className="text-[#d4af37] underline">Terms &amp; Conditions</a> and 
                  <a href="#" className="text-[#d4af37] underline"> Privacy Policy</a>.
                </span>
              </label>
            </div>

            {/* Price Summary */}
            <div className="bg-gradient-to-r from-[#0a1628] to-[#1a2d4a] rounded-xl p-6 border border-[#d4af37]/50">
              <div className="flex items-center justify-between mb-4">
                <span className="text-gray-300 text-lg">Total Package</span>
                <span className="text-3xl font-bold text-[#d4af37]">{formatPrice(totalPackage)}</span>
              </div>
              <div className="flex gap-4">
                <button
                  onClick={handleSubmitBooking}
                  className="flex-1 bg-[#d4af37]/20 hover:bg-[#d4af37]/30 border-2 border-[#d4af37] text-[#d4af37] py-3 rounded-xl font-bold text-lg flex items-center justify-center gap-2 transition-colors"
                >
                  Hold Booking - {formatPrice(depositAmount)} deposit
                </button>
                <button
                  onClick={handleSubmitBooking}
                  className="flex-1 bg-[#d4af37] hover:bg-[#e5c158] text-[#0a1628] py-3 rounded-xl font-bold text-lg flex items-center justify-center gap-2 transition-colors"
                >
                  <CreditCard className="w-5 h-5" />
                  Pay Full - {formatPrice(totalPackage)}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Package Summary View with Theme Colors
  return (
    <div className="min-h-screen bg-[#0a1628] py-6 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#1a2d4a] to-[#243b5c] rounded-t-2xl p-6 flex items-center justify-between border border-[#d4af37]/30">
          <div className="flex items-center gap-3">
            <Star className="w-7 h-7 text-[#d4af37]" />
            <div>
              <h1 className="text-2xl font-bold text-white">Package Summary</h1>
              <p className="text-gray-400 text-sm">Review your complete Umrah package</p>
            </div>
          </div>
          <button 
            onClick={onBack}
            className="flex items-center gap-2 bg-[#d4af37]/20 hover:bg-[#d4af37]/30 border border-[#d4af37] text-[#d4af37] px-4 py-2 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
        </div>

        <div className="flex flex-col lg:flex-row gap-6 mt-0">
          {/* Left Column - Package Details */}
          <div className="flex-1 space-y-4">
            {/* Flights Section */}
            <div className="bg-[#1a2d4a] rounded-xl shadow-sm overflow-hidden border border-[#d4af37]/20">
              <div className="bg-gradient-to-r from-[#d4af37] to-[#e5c158] px-4 py-3 flex items-center gap-2">
                <Plane className="w-5 h-5 text-[#0a1628]" />
                <h2 className="text-[#0a1628] font-bold">Flights</h2>
              </div>
              <div className="p-4 space-y-3">
                {/* Outbound Flight */}
                <div className="flex items-center justify-between p-3 bg-[#0a1628] rounded-lg border border-[#d4af37]/10">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#d4af37]/20 rounded-full flex items-center justify-center">
                      <Plane className="w-5 h-5 text-[#d4af37]" />
                    </div>
                    <div>
                      <div className="font-semibold text-white">
                        Outbound: {packageData?.outboundFlight?.legs?.[0]?.departure || searchParams?.departureAirport?.code || 'LHR'} → {packageData?.outboundFlight?.legs?.[0]?.arrival || 'JED'}
                      </div>
                      <div className="text-sm text-[#d4af37]">
                        {packageData?.outboundFlight?.legs?.[0]?.segments?.[0]?.airline_name || 'Qatar Airways'} • 
                        <Clock className="w-3 h-3 inline mx-1" />
                        {packageData?.outboundFlight?.legs?.[0]?.duration || '06:20'}
                      </div>
                    </div>
                  </div>
                  <div className="text-xl font-bold text-[#d4af37]">
                    {formatPrice(packageData?.outboundFlight?.pricing?.total_fare || 1263)}
                  </div>
                </div>

                {/* Return Flight */}
                <div className="flex items-center justify-between p-3 bg-[#0a1628] rounded-lg border border-[#d4af37]/10">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#d4af37]/20 rounded-full flex items-center justify-center">
                      <Plane className="w-5 h-5 text-[#d4af37] rotate-180" />
                    </div>
                    <div>
                      <div className="font-semibold text-white">
                        Return: {packageData?.inboundFlight?.legs?.[0]?.departure || 'MED'} → {packageData?.inboundFlight?.legs?.[0]?.arrival || searchParams?.departureAirport?.code || 'LHR'}
                      </div>
                      <div className="text-sm text-[#d4af37]">
                        {packageData?.inboundFlight?.legs?.[0]?.segments?.[0]?.airline_name || 'Gulf Air'} • 
                        <Clock className="w-3 h-3 inline mx-1" />
                        {packageData?.inboundFlight?.legs?.[0]?.duration || '06:20'}
                      </div>
                    </div>
                  </div>
                  <div className="text-xl font-bold text-[#d4af37]">
                    {formatPrice(packageData?.inboundFlight?.pricing?.total_fare || 832)}
                  </div>
                </div>
              </div>
            </div>

            {/* Hotels Section */}
            <div className="bg-[#1a2d4a] rounded-xl shadow-sm overflow-hidden border border-[#d4af37]/20">
              <div className="bg-gradient-to-r from-[#d4af37] to-[#e5c158] px-4 py-3 flex items-center gap-2">
                <Building2 className="w-5 h-5 text-[#0a1628]" />
                <h2 className="text-[#0a1628] font-bold">Hotels</h2>
              </div>
              <div className="p-4 space-y-3">
                {/* Makkah Hotel */}
                <div className="flex items-center justify-between p-3 bg-[#0a1628] rounded-lg border border-[#d4af37]/10">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#d4af37]/20 rounded-lg flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-[#d4af37]" />
                    </div>
                    <div>
                      <div className="font-semibold text-white">
                        {packageData?.makkahHotel?.name || 'Makkah Hotel'}
                      </div>
                      <div className="text-sm text-[#d4af37] flex items-center gap-2">
                        <Star className="w-3 h-3 fill-[#d4af37] text-[#d4af37]" />
                        <span>Makkah</span> • 
                        <span>{searchParams?.makkahNights || 5} nights</span> • 
                        <MapPin className="w-3 h-3" />
                        <span>{packageData?.makkahHotel?.distance_to_haram || '1km from Haram'}</span>
                      </div>
                    </div>
                  </div>
                  <div className="text-xl font-bold text-[#d4af37]">
                    {formatPrice(packageData?.makkahRoom?.total_price || packageData?.makkahHotel?.total_price * passengers || 300)}
                  </div>
                </div>

                {/* Madinah Hotel */}
                <div className="flex items-center justify-between p-3 bg-[#0a1628] rounded-lg border border-[#d4af37]/10">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#d4af37]/20 rounded-lg flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-[#d4af37]" />
                    </div>
                    <div>
                      <div className="font-semibold text-white">
                        {packageData?.madinahHotel?.name || 'Madinah Hotel'}
                      </div>
                      <div className="text-sm text-[#d4af37] flex items-center gap-2">
                        <Star className="w-3 h-3 fill-[#d4af37] text-[#d4af37]" />
                        <span>Medina</span> • 
                        <span>{searchParams?.madinahNights || 3} nights</span> • 
                        <MapPin className="w-3 h-3" />
                        <span>{packageData?.madinahHotel?.distance_to_haram || '1km from Haram'}</span>
                      </div>
                    </div>
                  </div>
                  <div className="text-xl font-bold text-[#d4af37]">
                    {formatPrice(packageData?.madinahRoom?.total_price || packageData?.madinahHotel?.total_price * passengers || 180)}
                  </div>
                </div>
              </div>
            </div>

            {/* Transport & Ziyarat Section */}
            <div className="bg-[#1a2d4a] rounded-xl shadow-sm overflow-hidden border border-[#d4af37]/20">
              <div className="bg-gradient-to-r from-[#d4af37] to-[#e5c158] px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Bus className="w-5 h-5 text-[#0a1628]" />
                  <h2 className="text-[#0a1628] font-bold">Transport &amp; Ziyarat</h2>
                </div>
                <span className="bg-[#0a1628] px-3 py-1 rounded-full text-[#d4af37] text-sm font-medium">
                  {packageData?.transport?.name || 'H1 Van'}
                </span>
              </div>
              <div className="p-4 space-y-4">
                {/* Transfer Routes */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <span className="font-semibold text-white">Transfer Routes ({packageData?.selectedTransfers?.length || 3})</span>
                  </div>
                  <div className="space-y-2">
                    {['Jeddah Airport → Makkah', 'Makkah → Medina', 'Medina Hotel → Medina Airport'].map((route, idx) => (
                      <div key={idx} className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2 text-gray-300">
                          <span className="text-[#d4af37]">→</span>
                          {route}
                        </div>
                        <span className="bg-[#d4af37]/20 text-[#d4af37] px-2 py-0.5 rounded text-xs font-medium">
                          ✓ Included
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Ziyarat Tours */}
                <div className="border-t border-[#d4af37]/20 pt-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Star className="w-4 h-4 text-[#d4af37]" />
                    <span className="font-semibold text-white">Ziyarat Tours</span>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2 text-gray-300">
                        <Star className="w-3 h-3 text-[#d4af37]" />
                        Makkah Ziyarat Tour
                      </div>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        packageData?.selectedZiyarat?.includes('makkah-ziyarat') 
                          ? 'bg-[#d4af37]/20 text-[#d4af37]' 
                          : 'bg-gray-700 text-gray-400'
                      }`}>
                        {packageData?.selectedZiyarat?.includes('makkah-ziyarat') ? 'Selected' : 'Not Selected'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2 text-gray-300">
                        <Star className="w-3 h-3 text-[#d4af37]" />
                        Medina Ziyarat Tour
                      </div>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        packageData?.selectedZiyarat?.includes('madinah-ziyarat') 
                          ? 'bg-[#d4af37]/20 text-[#d4af37]' 
                          : 'bg-gray-700 text-gray-400'
                      }`}>
                        {packageData?.selectedZiyarat?.includes('madinah-ziyarat') ? 'Selected' : 'Not Selected'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Umrah Visa Section */}
            <div className="bg-[#1a2d4a] rounded-xl shadow-sm overflow-hidden border border-[#d4af37]/20">
              <div className="bg-gradient-to-r from-[#d4af37] to-[#e5c158] px-4 py-3 flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#0a1628]" />
                <h2 className="text-[#0a1628] font-bold">Umrah Visa</h2>
              </div>
              <div className="p-4 space-y-3">
                {/* Visa breakdown by passport type */}
                <div className="flex items-center justify-between p-3 bg-[#0a1628] rounded-lg border border-[#d4af37]/10">
                  <div className="flex items-center gap-3">
                    <span className="text-xl font-bold text-[#d4af37]">🛂</span>
                    <div>
                      <div className="font-semibold text-white">
                        Umrah Visa ({passengers} passengers)
                      </div>
                      <div className="text-sm text-gray-400">
                        {searchParams?.adults || 2} Adults, {searchParams?.children || 0} Children, {searchParams?.infants || 0} Infants
                      </div>
                    </div>
                  </div>
                  <div className="text-xl font-bold text-[#d4af37]">
                    {formatPrice(visaTotal || 470)}
                  </div>
                </div>

                {/* Total Visa Cost */}
                <div className="flex items-center justify-between pt-3 border-t border-[#d4af37]/20">
                  <span className="font-bold text-white">Total Visa Cost</span>
                  <span className="text-xl font-bold text-[#d4af37]">{formatPrice(visaTotal || 470)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Price Breakdown */}
          <div className="w-full lg:w-80 flex-shrink-0">
            <div className="bg-[#1a2d4a] rounded-xl shadow-lg overflow-hidden sticky top-4 border border-[#d4af37]/30">
              <div className="bg-gradient-to-r from-[#d4af37] to-[#e5c158] px-4 py-3">
                <h2 className="text-[#0a1628] font-bold text-lg">Price Breakdown</h2>
              </div>
              <div className="p-4 space-y-3">
                {/* Flights */}
                <div className="flex items-center justify-between p-3 bg-[#0a1628] rounded-lg">
                  <div className="flex items-center gap-2">
                    <Plane className="w-4 h-4 text-[#d4af37]" />
                    <span className="font-medium text-white">Flights</span>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-gray-400">{formatPrice(Math.round(flightTotal / passengers))} pp</div>
                    <div className="font-bold text-[#d4af37]">{formatPrice(flightTotal || 2095)} total</div>
                  </div>
                </div>

                {/* Hotels */}
                <div className="flex items-center justify-between p-3 bg-[#0a1628] rounded-lg">
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-[#d4af37]" />
                    <span className="font-medium text-white">Hotels</span>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-gray-400">{formatPrice(Math.round(hotelTotal / passengers))} pp</div>
                    <div className="font-bold text-[#d4af37]">{formatPrice(hotelTotal || 480)} total</div>
                  </div>
                </div>

                {/* Transport & Ziyarat */}
                <div className="flex items-center justify-between p-3 bg-[#0a1628] rounded-lg">
                  <div className="flex items-center gap-2">
                    <Bus className="w-4 h-4 text-[#d4af37]" />
                    <span className="font-medium text-white">Transport</span>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-gray-400">{formatPrice(Math.round((transportTotal + ziyaratTotal) / passengers))} pp</div>
                    <div className="font-bold text-[#d4af37]">{formatPrice(transportTotal + ziyaratTotal || 336)} total</div>
                  </div>
                </div>

                {/* Visa */}
                <div className="flex items-center justify-between p-3 bg-[#0a1628] rounded-lg border border-dashed border-[#d4af37]/30">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-[#d4af37]" />
                    <span className="font-medium text-white">Visa ({passengers} pax)</span>
                  </div>
                  <div className="font-bold text-[#d4af37]">{formatPrice(visaTotal || 470)} total</div>
                </div>

                {/* Price Summary */}
                <div className="bg-gradient-to-r from-[#0a1628] to-[#1a2d4a] rounded-xl p-4 border border-[#d4af37]/50 mt-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-gray-300">Price per person</span>
                    <span className="text-xl font-bold text-[#d4af37]">{formatPrice(pricePerPerson || 845)} pp</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-white">Total Package</span>
                    <span className="text-2xl font-bold text-[#d4af37]">{formatPrice(totalPackage || 3381)}</span>
                  </div>
                  <div className="text-xs text-gray-400 text-right">For {passengers} passengers</div>
                </div>

                {/* Action Buttons */}
                <div className="space-y-3 pt-2">
                  <button 
                    onClick={initializePassengers}
                    className="w-full flex items-center justify-center gap-2 border-2 border-[#d4af37] text-[#d4af37] py-3 rounded-xl font-bold hover:bg-[#d4af37]/10 transition-colors"
                  >
                    Hold Booking - {formatPrice(depositAmount)} deposit
                  </button>
                  
                  <div className="relative flex items-center justify-center">
                    <div className="border-t border-[#d4af37]/30 w-full"></div>
                    <span className="absolute bg-[#1a2d4a] px-3 text-gray-400 text-sm">OR</span>
                  </div>
                  
                  <button 
                    onClick={initializePassengers}
                    className="w-full bg-[#d4af37] hover:bg-[#e5c158] text-[#0a1628] py-3 rounded-xl font-bold transition-colors"
                  >
                    Book Now - Pay {formatPrice(totalPackage || 3381)}
                  </button>
                  
                  <p className="text-xs text-gray-500 text-center">
                    You&apos;ll enter passenger details on the next step
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingSummary;
